import React, { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import {
  TextField, Button, IconButton, InputAdornment, CircularProgress, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,
  Box, Tooltip, Typography, InputBase, Dialog, DialogContent
} from "@mui/material";
import * as XLSX from 'xlsx';
import TableChart from '@mui/icons-material/TableChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import QuickMenu from "./QuickMenu";
import { GlobalStyles } from "@mui/material";
import MarkdownRenderer from "./MarkdownRenderer";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { Send, AttachFile, Mic, Close, ErrorOutline, Reply, Language } from "@mui/icons-material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useFavorites } from "../hooks/useFavorites";
import {
  ChatMain, MessagesWrapper, InputWrapper, MessageBubble, Overlay, StyledInputWrapper,
  MessageContentWrapper,
} from "./styled";
import BarChartIcon from '@mui/icons-material/BarChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ChartModal from './ChartModal';
import { SendButton } from './styled';
import { ChartSuggestion, fetchChartSuggestion } from "../hooks/useChartSuggestion";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import type { Message as ChatMessage, DatabaseInfo } from "../types/chat";
import dayjs from "dayjs";
import AdminPanel from './AdminPanel';
import { useAdmin } from '../hooks/useAdmin';
import { User, useUser } from '../components/UserContext';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { createMessageRating } from '../api/ratings';


// Dark theme colors - мемоизируем константы
const DARK_COLORS = Object.freeze({
  PRIMARY: "#000000",
  SECONDARY: "#1a1a1a",
  ACCENT: "#2d2d2d",
  SURFACE: "#121212",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  HOVER: "#404040",
  SUCCESS: "#4caf50",
  ERROR: "#ff4444",
  WARNING: "#ff9800",
  INFO: "#2196f3",
  ADMIN: "#9c27b0" // ← Добавьте эту строку!
});

const TRANSLATIONS: Record<string, any> = {
  Italian: {
    welcome: "Benvenuto",
    startConversation: "Inizia una conversazione",
    typeMessage: "Scrivi un messaggio...",
    suggestions: "Suggerimenti",
    adminPanel: "Pannello Admin",
    copy: "Copia",
    copied: "Copiato!",
    showMore: "Mostra di più",
    showLess: "Mostra meno",
    showAllRows: "Mostra tutte le righe",
    exportPdf: "Esporta PDF",
    exportExcel: "Esporta Excel",
    visualizeChart: "Visualizza Grafico",
    error: "Errore",
    retry: "Riprova",
    noData: "Nessun dato disponibile",
    loading: "Caricamento...",
    generatingChart: "Generazione grafico...",
    sqlQuery: "Query SQL",
    explanation: "Spiegazione",
    results: "Risultati",
    favorites: "Preferiti",
    addToFavorites: "Aggiungi ai preferiti",
    removeFromFavorites: "Rimuovi dai preferiti",
    useWeb: "Ricerca web"
  },
  French: {
    welcome: "Bienvenue",
    startConversation: "Commencer une conversation",
    typeMessage: "Écrivez un message...",
    suggestions: "Suggestions",
    adminPanel: "Panneau d'administration",
    copy: "Copier",
    copied: "Copié !",
    showMore: "Voir plus",
    showLess: "Voir moins",
    showAllRows: "Voir toutes les lignes",
    exportPdf: "Exporter PDF",
    exportExcel: "Exporter Excel",
    visualizeChart: "Visualiser le graphique",
    error: "Erreur",
    retry: "Réessayer",
    noData: "Aucune donnée disponible",
    loading: "Chargement...",
    generatingChart: "Génération du graphique...",
    sqlQuery: "Requête SQL",
    explanation: "Explication",
    results: "Résultats",
    favorites: "Favoris",
    addToFavorites: "Ajouter aux favoris",
    removeFromFavorites: "Retirer des favoris",
    useWeb: "Recherche web"
  }
};

// Memoized export functions
const handleDownloadPdf = (msg: ChatMessage) => {
  const doc = new jsPDF();
  const headers = [msg.tableData?.headers ?? []];
  const rows = msg.tableData?.rows?.map(row =>
    (msg.tableData?.headers ?? []).map(h => String(row[h] ?? ""))
  );
  doc.text(msg.text || 'Risultati Tabella', 14, 14);
  autoTable(doc, {
    head: headers,
    body: rows,
    startY: 22,
    styles: { fontSize: 10 }
  });
  doc.save('tabella.pdf');
};


const handleDownloadExcel = (msg: ChatMessage) => {
  const headers = msg.tableData?.headers ?? [];
  const rows = msg.tableData?.rows ?? [];


  if (headers.length === 0 || rows.length === 0) {
    return;
  }


  const data = [
    headers,
    ...rows.map(row => headers.map(h => String(row[h] ?? '')))
  ];


  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Foglio1');
  XLSX.writeFile(workbook, 'tabella.xlsx');
};


const DBS: DatabaseInfo[] = [
  { id: "wt_mouldplast", name: "Wt_Mouldplast" },
  { id: "self", name: "Self" },
  { id: "wpdemo1", name: "Workplan" },
];
const defaultDB = DBS.find(d => d.id === "self")!;


interface ChatMainViewProps {
  input: string;
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setInput: (val: string) => void;
  messages: ChatMessage[];
  isLoading: boolean;
  sendMessage: (replyToId?: string, useWeb?: boolean) => void;
  showPopup: boolean;
  setShowPopup: (val: boolean) => void;
  handleToggleTableExpand: (id: string) => void;
  chatId?: string;
  getMessagePair?: (botMessageId: string) => { userMessage: ChatMessage; botMessage: ChatMessage } | null;
  targetDbId?: string;
  databaseId?: number;
}

const TableView = memo(({ 
  msg, 
  isTableExpanded, 
  collapsedRowCount, 
  onToggleExpand,
  onDownloadPdf,
  onDownloadExcel,
  onShowChart,
  chartLoadingId,
  showStar,
  isMessageFavorite,
  onToggleFavorite,
  t
}: {
  msg: ChatMessage;
  isTableExpanded: boolean;
  collapsedRowCount: number;
  onToggleExpand: (id: string) => void;
  onDownloadPdf: (msg: ChatMessage) => void;
  onDownloadExcel: (msg: ChatMessage) => void;
  onShowChart: (msg: ChatMessage) => void;
  chartLoadingId: string | null;
  showStar: boolean;
  isMessageFavorite: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
  t: any;
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const renderSafeCell = useCallback((val: any, header?: string) => {
    if (val == null) return '';
    
    // Special handling for sito_web column with markdown links
    if (header === 'sito_web' && typeof val === 'string') {
      // Parse markdown link format [text](url)
      const markdownLinkMatch = val.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (markdownLinkMatch) {
        const [, text, url] = markdownLinkMatch;
        return (
          <a 
            href={url.startsWith('http') ? url : `https://${url}`}
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: '#60a5fa',
              textDecoration: 'none',
              borderBottom: '1px solid transparent',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderBottomColor = '#60a5fa';
              e.currentTarget.style.color = '#93c5fd';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderBottomColor = 'transparent';
              e.currentTarget.style.color = '#60a5fa';
            }}
          >
            {text} ↗
          </a>
        );
      }
    }
    
    // Special handling for punti_di_forza column - render multiline text
    if (header === 'punti_di_forza' && typeof val === 'string') {
      return (
        <span style={{ whiteSpace: 'pre-line', display: 'block' }}>
          {val}
        </span>
      );
    }
    
    // Special handling for descriptions - render multiline text
    if ((header === 'descrizione' || header === 'description') && typeof val === 'string') {
      return (
        <span style={{ whiteSpace: 'pre-line', display: 'block', maxWidth: '400px' }}>
          {val}
        </span>
      );
    }
    
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val);
      } catch {
        return '[Oggetto non valido]';
      }
    }
    return String(val);
  }, []);

  // Format table headers
  const formatHeader = useCallback((header: string): string => {
    // Map Italian headers
    const headerMap: Record<string, string> = {
      'rivenditore': '🏢 Rivenditore',
      'sito_web': '🌐 Sito Web',
      'descrizione': '📝 Descrizione',
      'punti_di_forza': '⭐ Punti di forza',
    };
    
    if (headerMap[header]) {
      return headerMap[header];
    }
    
    // Convert snake_case to Title Case
    return header
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, []);

  const visibleRows = useMemo(() => {
    return msg.tableData!.rows.slice(0, isTableExpanded ? msg.tableData!.rows.length : collapsedRowCount);
  }, [msg.tableData, isTableExpanded, collapsedRowCount]);

  const tableContent = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          px: 1.5,
          py: 1,
          backgroundColor: DARK_COLORS.ACCENT,
          borderBottom: `1px solid ${DARK_COLORS.BORDER}`,
        }}
      >
        {showStar && (
          <Tooltip title={isMessageFavorite ? t.removeFromFavorites : t.addToFavorites}>
            <IconButton
              size="small"
              aria-label="preferito"
              aria-pressed={isMessageFavorite}
              onClick={onToggleFavorite}
              sx={{
                mr: 0.5,
                color: isMessageFavorite ? '#FFD700' : DARK_COLORS.TEXT_SECONDARY,
                backgroundColor: 'transparent',
                borderRadius: '10px',
                width: 28,
                height: 28,
                transition: 'all .15s ease',
                '&:hover': {
                  color: '#FFD700',
                  backgroundColor: 'rgba(255,215,0,.10)',
                  transform: 'scale(1.08)'
                },
                '&:focus-visible': {
                  outline: '2px solid rgba(255,215,0,.6)',
                  outlineOffset: 2,
                }
              }}
            >
              {isMessageFavorite ? (
                <StarRoundedIcon fontSize="small" />
              ) : (
                <StarBorderRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={t.exportPdf}>
          <IconButton
            size="small"
            aria-label="Scarica PDF"
            onClick={() => onDownloadPdf(msg)}
            sx={{
              color: DARK_COLORS.ERROR,
              opacity: 0.8,
              '&:hover': { 
                color: DARK_COLORS.TEXT_PRIMARY, 
                opacity: 1, 
                background: DARK_COLORS.HOVER,
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {!isFullscreen && (
          <Tooltip title={t.visualizeChart}>
            <IconButton
              size="small"
              aria-label="Mostra il grafico"
              onClick={() => onShowChart(msg)}
              sx={{
                color: '#1976d2',
                opacity: 0.5,
                mr: 0.5,
                '&:hover': { color: '#1565c0', opacity: 1, background: '#e3f2fd' },
                transition: 'opacity 0.18s'
              }}
            >
              {chartLoadingId === msg.id ? (
                <CircularProgress size={18} sx={{ color: '#1976d2' }} />
              ) : (
                <BarChartIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={t.exportExcel}>
          <IconButton
            size="small"
            aria-label="Scarica Excel"
            onClick={() => onDownloadExcel(msg)}
            sx={{
              color: "green",
              opacity: 0.8,
              '&:hover': { 
                color: DARK_COLORS.TEXT_PRIMARY, 
                opacity: 1, 
                background: DARK_COLORS.HOVER,
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <TableChart fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t.copy + " SQL"}>
          <IconButton
            size="small"
            aria-label="Copia SQL"
            onClick={() => {
              if (msg.sqlQuery) {
                navigator.clipboard.writeText(msg.sqlQuery)
                  .then(() => alert(t.copied))
                  .catch(() => alert(t.error));
              } else {
                alert(t.noData);
              }
            }}
            sx={{
              color: '#ff9800',
              opacity: 0.8,
              '&:hover': { 
                color: DARK_COLORS.TEXT_PRIMARY, 
                opacity: 1, 
                background: DARK_COLORS.HOVER,
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease',
              ml: 0.5
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={isFullscreen ? "Esci dalla modalità schermo intero" : "Espandi a schermo intero"}>
          <IconButton
            size="small"
            aria-label={isFullscreen ? "Esci schermo intero" : "Schermo intero"}
            onClick={() => setIsFullscreen(!isFullscreen)}
            sx={{
              color: '#9c27b0',
              opacity: 0.8,
              ml: 0.5,
              '&:hover': { 
                color: DARK_COLORS.TEXT_PRIMARY, 
                opacity: 1, 
                background: DARK_COLORS.HOVER,
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer
        sx={{
          maxHeight: isFullscreen ? 'calc(100vh - 200px)' : (isTableExpanded ? 'none' : '350px'),
          backgroundColor: DARK_COLORS.SURFACE,
          '&::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: DARK_COLORS.ACCENT,
            borderRadius: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: DARK_COLORS.TEXT_SECONDARY,
            borderRadius: '5px',
            '&:hover': {
              background: DARK_COLORS.TEXT_PRIMARY,
            }
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {msg.tableData!.headers.map(header => (
                <TableCell
                  key={header}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: DARK_COLORS.SECONDARY,
                    color: DARK_COLORS.TEXT_PRIMARY,
                    borderBottom: `2px solid ${DARK_COLORS.BORDER}`,
                    fontSize: 14,
                    padding: '12px 16px',
                  }}
                >
                  {formatHeader(header)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, i) => (
              <TableRow
                key={i}
                sx={{
                  '&:nth-of-type(odd)': { 
                    backgroundColor: DARK_COLORS.SURFACE
                  },
                  '&:nth-of-type(even)': { 
                    backgroundColor: DARK_COLORS.ACCENT
                  },
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:hover': {
                    backgroundColor: DARK_COLORS.HOVER,
                  },
                  transition: 'background-color 0.2s ease',
                }}
              >
                {msg.tableData!.headers.map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      borderBottom: `1px solid ${DARK_COLORS.BORDER}`,
                      fontSize: 13,
                      color: DARK_COLORS.TEXT_PRIMARY,
                      padding: '8px 16px',
                    }}
                  >
                    {renderSafeCell(row[h], h)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Кнопка "показать больше/меньше" теперь показывается и в полноэкранном режиме */}
      {msg.tableData!.rows.length > collapsedRowCount && (
        <Button
          onClick={() => onToggleExpand(msg.id)}
          size="small"
          variant="text"
          sx={{
            ml: 1,
            mb: 1,
            mt: 1,
            color: '#a855f7',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(168,85,247,0.1)',
              color: '#c084fc',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(168,85,247,0.2)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '12px',
            px: 2,
            py: 1,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.2), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover::before': {
              left: '100%',
            }
          }}
        >
          {isTableExpanded
            ? t.showLess + ' ▲'
            : t.showAllRows.replace('{count}', msg.tableData!.rows.length) + ' ▼'}
        </Button>
      )}
    </>
  );

  if (isFullscreen) {
    return (
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: DARK_COLORS.SURFACE,
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
            `,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${DARK_COLORS.BORDER}, transparent)`,
              opacity: 0.5,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${DARK_COLORS.BORDER}, transparent)`,
              opacity: 0.5,
            }
          }
        }}
      >
        <DialogContent sx={{ 
          p: 0,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '1px',
            height: '100%',
            background: `linear-gradient(180deg, transparent, ${DARK_COLORS.BORDER}, transparent)`,
            opacity: 0.3,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '1px',
            height: '100%',
            background: `linear-gradient(180deg, transparent, ${DARK_COLORS.BORDER}, transparent)`,
            opacity: 0.3,
          }
        }}>
          {tableContent}
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 720,
        mt: 2,
        mb: 2,
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: `1px solid ${DARK_COLORS.BORDER}`,
        backgroundColor: DARK_COLORS.SURFACE,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        }
      }}
    >
      {tableContent}
    </Paper>
  );
});


// Memoized message component
const MessageComponent = memo(({ 
  msg, 
  messages,
  getMessagePair, 
  handleToggleTableExpand, 
  onDownloadPdf, 
  onDownloadExcel, 
  handleShowChart, 
  chartLoadingId, 
  favorites, 
  isFavoriteByText, 
  addFavorite, 
  removeFavoriteByText,
  setQuickSuggestions,
  onReply,
  replyingToId,
  handleRating,
  ratedMessages,
  ratingInProgress,
  t
}: {
  msg: ChatMessage;
  messages: ChatMessage[];
  getMessagePair?: (botMessageId: string) => { userMessage: ChatMessage; botMessage: ChatMessage } | null;
  handleToggleTableExpand: (id: string) => void;
  onDownloadPdf: (msg: ChatMessage) => void;
  onDownloadExcel: (msg: ChatMessage) => void;
  handleShowChart: (msg: ChatMessage) => void;
  chartLoadingId: string | null;
  favorites: any;
  isFavoriteByText: (text: string) => boolean;
  addFavorite: (userMsg: ChatMessage, botMsg: ChatMessage, sqlQuery: string) => Promise<void>;
  removeFavoriteByText: (text: string) => Promise<void>;
  setQuickSuggestions: React.Dispatch<React.SetStateAction<string[]>>;
  onReply?: (message: ChatMessage) => void;
  replyingToId?: string | null;
  handleRating?: (e: React.MouseEvent, botMessageId: string, isValid: boolean) => Promise<void>;
  ratedMessages?: Set<string>;
  ratingInProgress?: Set<string>;
  t: any;
}) => {
  const shouldRenderTable = useMemo(() => {
    if(msg.type === 'bot' && msg.tableData) {
      const { headers, rows } = msg.tableData;
      return Array.isArray(headers) && headers.length > 0 && Array.isArray(rows) && rows.length > 0;
    }
    return false;
  }, [msg.type, msg.tableData]);


  const showStar = useMemo(() => {
    return msg.type === 'bot' && shouldRenderTable && !!msg.sqlQuery;
  }, [msg.type, shouldRenderTable, msg.sqlQuery]);


  const pair = useMemo(() => {
    return getMessagePair ? getMessagePair(msg.id) : null;
  }, [getMessagePair, msg.id]);


  const userText = useMemo(() => {
    return pair?.userMessage?.text || "";
  }, [pair]);


  const isMessageFavorite = useMemo(() => {
    return !!userText && isFavoriteByText(userText);
  }, [userText, isFavoriteByText]);


  const onToggleFavorite = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!getMessagePair) return;
    const pair = getMessagePair(msg.id);
    if (!pair?.userMessage) return;
    const { userMessage, botMessage } = pair;
    const userText = userMessage.text || "";


    try {
      if (isFavoriteByText(userText)) {
        setQuickSuggestions(prev => prev.filter(s => s !== userText));
        await removeFavoriteByText(userText);
      } else {
        setQuickSuggestions(prev => {
          const newSet = new Set([...prev, userText]);
          return Array.from(newSet).slice(0, 12);
        });
        await addFavorite(userMessage, botMessage, botMessage.sqlQuery || "");
      }
    } catch (err) {
      if (isFavoriteByText(userText)) {
        setQuickSuggestions(prev => [...new Set([...prev, userText])].slice(0, 12));
      } else {
        setQuickSuggestions(prev => prev.filter(s => s !== userText));
      }
      alert('Errore');
    }
  }, [getMessagePair, msg.id, isFavoriteByText, addFavorite, removeFavoriteByText, setQuickSuggestions]);


  const collapsedRowCount = 10;

  // Parse markdown table from explanation
  const parseMarkdownTable = useCallback((text: string): { beforeTable: string; afterTable: string } | null => {
    if (!text) return null;
    
    // Find markdown table in text
    const tableRegex = /\|[^\n]+\|\n\|[-:| ]+\|\n(\|[^\n]+\|\n?)+/;
    const match = text.match(tableRegex);
    
    if (match) {
      const tableStart = match.index || 0;
      const tableEnd = tableStart + match[0].length;
      
      return {
        beforeTable: text.substring(0, tableStart).trim(),
        afterTable: text.substring(tableEnd).trim(),
      };
    }
    
    return null;
  }, []);

  const parsedExplanation = useMemo(() => {
    if (msg.explanation && msg.explanation.includes('|')) {
      return parseMarkdownTable(msg.explanation);
    }
    return null;
  }, [msg.explanation, parseMarkdownTable]);

  const repliedToMessage = useMemo(() => {
    if (!msg.replyTo && !msg.replyToMessageId) return null;
    if (msg.replyTo) {
      const local = messages.find(m => m.id === msg.replyTo);
      if (local) return local;
    }
    if (msg.replyToMessageId) {
      const byBackend = messages.find(m => (m as any).backendId === msg.replyToMessageId);
      if (byBackend) return byBackend;
    }
    return null;
  }, [msg.replyTo, msg.replyToMessageId, messages]);

  const replyPreviewText = useMemo(() => {
    if (!repliedToMessage) return "";
    const text = repliedToMessage.text || repliedToMessage.explanation || "Message";
    const clean = text.replace(/[*_`#()\[\]]/g, "").trim();
    return clean.length > 80 ? `${clean.slice(0, 80)}...` : clean;
  }, [repliedToMessage]);

  const hasReplyReference = Boolean(msg.replyTo) || (msg.replyToMessageId !== undefined && msg.replyToMessageId > 0);
  const replyLabel = msg.replyToMessageId
    ? (replyPreviewText ? `Reply to #${msg.replyToMessageId}: ${replyPreviewText}` : `Reply to #${msg.replyToMessageId}`)
    : (replyPreviewText ? `Reply to: ${replyPreviewText}` : "Reply to message");


  return (
    <MessageBubble key={msg.id} isUser={msg.type === 'user'}>
      <MessageContentWrapper isUser={msg.type === 'user'}>
        <div data-msg-wrapper style={{ position: 'relative' }}>
          {hasReplyReference && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, opacity: 0.9 }}>
              <Box sx={{
                fontSize: '0.75rem',
                color: DARK_COLORS.TEXT_SECONDARY,
                backgroundColor: DARK_COLORS.ACCENT,
                border: `1px solid ${DARK_COLORS.BORDER}`,
                borderRadius: '8px',
                padding: '4px 8px',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {replyLabel}
              </Box>
            </Box>
          )}
          {msg.type === 'user' ? (
            // User messages - plain text without markdown
            <div style={{ color: DARK_COLORS.TEXT_PRIMARY, whiteSpace: 'pre-wrap' }}>
              {msg.text}
            </div>
          ) : parsedExplanation ? (
            <>
              {parsedExplanation.beforeTable && (
                <MarkdownRenderer 
                  content={parsedExplanation.beforeTable} 
                  className="markdown-content"
                />
              )}
              {shouldRenderTable && (
                <Box sx={{ my: 2 }}>
                  <TableView
                    msg={msg}
                    isTableExpanded={!!msg.isTableExpanded}
                    collapsedRowCount={collapsedRowCount}
                    onToggleExpand={handleToggleTableExpand}
                    onDownloadPdf={onDownloadPdf}
                    onDownloadExcel={onDownloadExcel}
                    onShowChart={handleShowChart}
                    chartLoadingId={chartLoadingId}
                    showStar={showStar}
                    isMessageFavorite={isMessageFavorite}
                    onToggleFavorite={onToggleFavorite}
                    t={t}
                  />
                </Box>
              )}
              {parsedExplanation.afterTable && (
                <MarkdownRenderer 
                  content={parsedExplanation.afterTable} 
                  className="markdown-content markdown-hint"
                />
              )}
            </>
          ) : (
            <>
              {/* Отображаем explanation если есть, иначе text */}
              {msg.explanation ? (
                <MarkdownRenderer 
                  content={msg.explanation} 
                  className="markdown-content"
                />
              ) : msg.text ? (
                <MarkdownRenderer 
                  content={msg.text} 
                  className="markdown-content"
                />
              ) : (
                <div style={{ color: DARK_COLORS.TEXT_SECONDARY, fontStyle: 'italic' }}>
                  [Nessun contenuto]
                </div>
              )}
              {shouldRenderTable && (
                <Box sx={{ my: 2 }}>
                  <TableView
                    msg={msg}
                    isTableExpanded={!!msg.isTableExpanded}
                    collapsedRowCount={collapsedRowCount}
                    onToggleExpand={handleToggleTableExpand}
                    onDownloadPdf={onDownloadPdf}
                    onDownloadExcel={onDownloadExcel}
                    onShowChart={handleShowChart}
                    chartLoadingId={chartLoadingId}
                    showStar={showStar}
                    isMessageFavorite={isMessageFavorite}
                    onToggleFavorite={onToggleFavorite}
                    t={t}
                  />
                </Box>
              )}
            </>
          )}
          {msg.type === 'bot' && msg.timestamp && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginTop: '10px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <div style={{
                  fontSize: "0.75em",
                  opacity: 0.7,
                  color: DARK_COLORS.TEXT_SECONDARY
                }}>
                  {dayjs(msg.timestamp).format("HH:mm DD/MM")}
                </div>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {onReply && (
                  <Tooltip title="Reply" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onReply(msg)}
                      sx={{
                        color: replyingToId === msg.id ? DARK_COLORS.INFO : DARK_COLORS.TEXT_SECONDARY,
                        '&:hover': {
                          color: DARK_COLORS.INFO,
                          backgroundColor: 'rgba(33, 150, 243, 0.12)',
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Reply fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              {handleRating && ratedMessages && !ratedMessages.has(msg.id) && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Risposta utile" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => handleRating(e, msg.id, true)}
                      disabled={ratingInProgress?.has(msg.id)}
                      sx={{
                        color: DARK_COLORS.TEXT_SECONDARY,
                        '&:hover': {
                          color: DARK_COLORS.SUCCESS,
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      {ratingInProgress?.has(msg.id) ? (
                        <CircularProgress size={16} />
                      ) : (
                        <ThumbUpIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Risposta non utile" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => handleRating(e, msg.id, false)}
                      disabled={ratingInProgress?.has(msg.id)}
                      sx={{
                        color: DARK_COLORS.TEXT_SECONDARY,
                        '&:hover': {
                          color: DARK_COLORS.ERROR,
                          backgroundColor: 'rgba(255, 68, 68, 0.1)',
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      {ratingInProgress?.has(msg.id) ? (
                        <CircularProgress size={16} />
                      ) : (
                        <ThumbDownIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {handleRating && ratedMessages && ratedMessages.has(msg.id) && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: DARK_COLORS.TEXT_SECONDARY,
                    fontStyle: 'italic',
                    opacity: 0.7
                  }}
                >
                  Valutato ✓
                </Typography>
              )}
              </Box>
            </Box>
          )}
          {msg.type === 'error' && (
            <ErrorOutline 
              color="error" 
              sx={{ 
                fontSize: '16px', 
                ml: '5px',
                color: DARK_COLORS.ERROR,
                filter: 'drop-shadow(0 0 4px rgba(255, 68, 68, 0.3))'
              }} 
            />
          )}
        </div>
      </MessageContentWrapper>
    </MessageBubble>
  );
});


const ChatMainView: React.FC<ChatMainViewProps> = ({
  input, setInput, messages, isLoading, sendMessage, showPopup, setShowPopup,
  handleToggleTableExpand, isSidebarCollapsed, toggleSidebar, chatId, getMessagePair, targetDbId, databaseId
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRefEl = useRef<HTMLTextAreaElement | null>(null);
  
  const [chartMessage, setChartMessage] = useState<ChatMessage | null>(null);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [chartLoadingId, setChartLoadingId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [quickSuggestions, setQuickSuggestions] = useState<string[]>([]);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [useWeb, setUseWeb] = useState(false);
  const [chartData, setChartData] = useState<{
    headers: string[],
    rows: Record<string, any>[],
    suggestion: ChartSuggestion,
  } | null>(null);

  const { user, language } = useUser();
  const t = TRANSLATIONS[language] || TRANSLATIONS["Italian"];
  const { isAdmin } = useAdmin(user ?? null);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);

  const { favorites, favoritesByText, addFavorite, removeFavoriteByText, isFavoriteByText } = useFavorites();


  // Memoize favorite calculations
  const favoriteSuggestions = useMemo(() => {
    return Object.values(favoritesByText || {})
      .map((f: any) => f?.question_text)
      .filter(Boolean)
      .slice(0, 8);
  }, [favoritesByText]);


  // Memoize functions
  const openQuickMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget as HTMLElement);
  }, []);


  const closeQuickMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);


  const applySuggestion = useCallback(async (text: string, autoSend = false) => {
    setInput(text);
    if (autoSend) await sendMessage(undefined, useWeb);
  }, [setInput, sendMessage, useWeb]);


  const handleDeleteSuggestion = useCallback(async (text: string) => {
    if (isFavoriteByText(text)) {
      try {
        setQuickSuggestions(prev => prev.filter(s => s !== text));
        await removeFavoriteByText(text);
        closeQuickMenu();
      } catch (err) {
        setQuickSuggestions(prev => [...new Set([...prev, text])].slice(0, 12));
        alert('Errore');
      }
    }
  }, [isFavoriteByText, removeFavoriteByText, closeQuickMenu]);


  const handleReorderSuggestions = useCallback((newOrder: string[]) => {
    setQuickSuggestions(newOrder);
  }, []);


   const handleShowChart = useCallback(async (msg: ChatMessage) => {
    if (!msg.tableData) return;
    try {
      setChartLoadingId(msg.id);
      const suggestion = await fetchChartSuggestion(msg.tableData.headers, msg.tableData.rows, user?.id);
      const normalizedSuggestion: ChartSuggestion = {
        ...suggestion,
        y_axis: Array.isArray(suggestion.y_axis)
          ? suggestion.y_axis
          : [suggestion.y_axis],
      };
      setChartData({
        headers: msg.tableData.headers,
        rows: msg.tableData.rows,
        suggestion: normalizedSuggestion,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Errore sconosciuto";
      alert("Errore nella generazione del grafico: " + errorMessage);
    } finally {
      setChartLoadingId(null);
    }
  }, [user]);


  const handleSendMessage = useCallback(async () => {
    if (!input.trim()) return;
    if (inputRefEl.current) {
      inputRefEl.current.style.height = "80px";
    }
    const replyToId = replyingTo?.id;
    await sendMessage(replyToId, useWeb);
    if (replyingTo) {
      setReplyingTo(null);
    }
    setUseWeb(false);
  }, [input, sendMessage, replyingTo, useWeb]);


  // Effect for updating suggestions - optimized
  useEffect(() => {
    const newSuggestions = Array.from(
      new Set([...favoriteSuggestions])
    ).slice(0, 12);
    
    setQuickSuggestions(newSuggestions);
  }, [favoriteSuggestions]);


  // Effect for auto-scroll - debounced
  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages, isLoading]);


  // Memoize keyboard and text change event handlers
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      setTimeout(() => inputRefEl.current?.focus(), 0);
    }
  }, [handleSendMessage]);


  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [setInput]);

  const handleReply = useCallback((message: ChatMessage) => {
    if (message.type !== 'bot') return;
    setReplyingTo(message);
    setTimeout(() => inputRefEl.current?.focus(), 0);
  }, []);

  const handleClearReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  // State for tracking rated messages - Load from localStorage
  const [ratedMessages, setRatedMessages] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('ratedMessages');
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(parsed);
      }
    } catch (error) {
      // Failed to load rated messages from localStorage
    }
    return new Set();
  });
  const [ratingInProgress, setRatingInProgress] = useState<Set<string>>(new Set());

  // Save rated messages to localStorage whenever it changes
  useEffect(() => {
    try {
      const array = Array.from(ratedMessages);
      localStorage.setItem('ratedMessages', JSON.stringify(array));
    } catch (error) {
      // Failed to save rated messages to localStorage
    }
  }, [ratedMessages]);

  // Rating handler
  const handleRating = useCallback(async (e: React.MouseEvent, botMessageId: string, isValid: boolean) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!getMessagePair || !databaseId || !user) return;
    if (ratingInProgress.has(botMessageId)) return; // Prevent multiple clicks
    
    const pair = getMessagePair(botMessageId);
    if (!pair) {
      return;
    }

    const { userMessage, botMessage } = pair;
    
    // Mark as in progress
    setRatingInProgress(prev => new Set(prev).add(botMessageId));
    
    try {
      // Construct messages array as per API format
      const messagesPayload: Array<{
        role: 'user' | 'assistant';
        content: string | { sql?: string; explanation?: string };
      }> = [
        {
          role: 'user' as const,
          content: userMessage.text || ''
        },
        {
          role: 'assistant' as const,
          content: {
            sql: botMessage.sqlQuery || '',
            explanation: botMessage.explanation || botMessage.text || ''
          }
        }
      ];

      await createMessageRating({
        database_id: databaseId,
        is_valid: isValid,
        messages: messagesPayload
      });

      // Mark as rated
      setRatedMessages(prev => new Set(prev).add(botMessageId));
    } catch (error) {
      alert('Errore durante l\'invio della valutazione');
    } finally {
      // Remove from in progress
      setRatingInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(botMessageId);
        return newSet;
      });
    }
  }, [getMessagePair, databaseId, user]);


  return (
    <ChatMain isSidebarCollapsed={isSidebarCollapsed}
    >
        <GlobalStyles styles={{
        '.MessageBubble:hover .star-hover-wrapper': { opacity: 1 },
        '[data-msg-wrapper]:hover .star-hover-wrapper': { opacity: 1 },
        '[data-msg-wrapper]:focus-within .star-hover-wrapper': { opacity: 1 },
        '@media (hover: none)': { '.star-hover-wrapper': { opacity: 1 } }
      }}/>
     {/* Кнопка админ-панели */}
    {isAdmin && (
      <Tooltip title={t.adminPanel} arrow placement="left">
        <IconButton
          onClick={() => setAdminPanelOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 24,
            width: 56,
            height: 56,
            background: `linear-gradient(135deg, ${DARK_COLORS.INFO} 0%, ${DARK_COLORS.SUCCESS} 100%)`,
            color: DARK_COLORS.TEXT_PRIMARY,
            border: `2px solid ${DARK_COLORS.ACCENT}`,
            boxShadow: `0 4px 20px rgba(33, 150, 243, 0.4), 0 0 40px rgba(76, 175, 80, 0.2)`,
            zIndex: 1000,
            backdropFilter: 'blur(10px)',
            '&:hover': {
              background: `linear-gradient(135deg, ${DARK_COLORS.SUCCESS} 0%, ${DARK_COLORS.INFO} 100%)`,
              transform: 'scale(1.15) rotate(180deg)',
              boxShadow: `0 8px 32px rgba(33, 150, 243, 0.6), 0 0 60px rgba(76, 175, 80, 0.4)`,
              borderColor: DARK_COLORS.TEXT_PRIMARY,
            },
            '&:active': {
              transform: 'scale(1.05) rotate(180deg)',
            },
            transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            animation: 'pulse 3s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': {
                boxShadow: `0 4px 20px rgba(33, 150, 243, 0.4), 0 0 40px rgba(76, 175, 80, 0.2)`,
              },
              '50%': {
                boxShadow: `0 4px 20px rgba(33, 150, 243, 0.6), 0 0 60px rgba(76, 175, 80, 0.4)`,
              }
            }
          }}
        >
          <AdminPanelSettingsIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Tooltip>
    )}

    {/* Админ-панель */}
    <AdminPanel
      open={adminPanelOpen}
      onClose={() => setAdminPanelOpen(false)}
      userRole={user?.role || 'user'}
      userName={user?.username || user?.fullName || 'Admin'}
    />
      <GlobalStyles styles={{
        '.MessageBubble:hover .star-hover-wrapper': { opacity: 1 },
        '[data-msg-wrapper]:hover .star-hover-wrapper': { opacity: 1 },
        '[data-msg-wrapper]:focus-within .star-hover-wrapper': { opacity: 1 },
        '@media (hover: none)': { '.star-hover-wrapper': { opacity: 1 } }
      }}/>
      <MessagesWrapper>
        <Box sx={{ maxWidth: 960, mx: 'auto', width: '100%', px: 3 }}>
          {messages.length === 0 ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60vh',
              opacity: 0.7,
            }}>
              <Typography variant="h4" sx={{ color: DARK_COLORS.TEXT_PRIMARY, mb: 2, fontWeight: 600 }}>
                {t.welcome}, {user?.username || user?.fullName || 'User'}
              </Typography>
              <Typography variant="body1" sx={{ color: DARK_COLORS.TEXT_SECONDARY }}>
                {t.startConversation}
              </Typography>
            </Box>
          ) : (
            messages
              .filter((msg, index) => {
                // Фильтруем дубликаты: если текущее сообщение содержит только SQL без explanation и tableData,
                // и следующее сообщение имеет explanation и tableData с тем же SQL, пропускаем текущее
                if (msg.type === 'bot' && !msg.explanation && !msg.tableData && msg.sqlQuery) {
                  const nextMsg = messages[index + 1];
                  if (nextMsg && 
                      nextMsg.type === 'bot' && 
                      nextMsg.explanation && 
                      nextMsg.tableData && 
                      nextMsg.sqlQuery === msg.sqlQuery) {
                    // Это дубликат - пропускаем
                    return false;
                  }
                  // Также проверяем, содержит ли text SQL код
                  if (msg.text && (msg.text.includes('SQL generato') || msg.text.includes('Risultati:'))) {
                    const nextMsg = messages[index + 1];
                    if (nextMsg && 
                        nextMsg.type === 'bot' && 
                        (nextMsg.explanation || nextMsg.tableData)) {
                      // Это сырое сообщение перед отформатированным - пропускаем
                      return false;
                    }
                  }
                }
                return true;
              })
              .map((msg) => (
            <MessageComponent
              key={msg.id}
              msg={msg}
              messages={messages}
              getMessagePair={getMessagePair}
              handleToggleTableExpand={handleToggleTableExpand}
              onDownloadPdf={handleDownloadPdf}
              onDownloadExcel={handleDownloadExcel}
              handleShowChart={handleShowChart}
              chartLoadingId={chartLoadingId}
              favorites={favorites}
              isFavoriteByText={isFavoriteByText}
              addFavorite={addFavorite}
              removeFavoriteByText={removeFavoriteByText}
              setQuickSuggestions={setQuickSuggestions}
              onReply={handleReply}
              replyingToId={replyingTo?.id ?? null}
              handleRating={handleRating}
              ratedMessages={ratedMessages}
              ratingInProgress={ratingInProgress}
              t={t}
            />
          )))}
          <div ref={messagesEndRef} />
        </Box>
      </MessagesWrapper>


      {showPopup && (
        <>
          <Overlay onClick={() => setShowPopup(false)} />
          <Alert
            severity="info"
            sx={{
              position: "fixed",
              top: "10%",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1301,
              backgroundColor: DARK_COLORS.SURFACE,
              color: DARK_COLORS.TEXT_PRIMARY,
              border: `1px solid ${DARK_COLORS.BORDER}`,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.8)",
              '& .MuiAlert-icon': {
                color: DARK_COLORS.INFO
              }
            }}
          >
            Sto lavorando sulla funzionalità del microfono...
          </Alert>
        </>
      )}


      <StyledInputWrapper>
        <Box sx={{ maxWidth: 960, mx: 'auto', width: '100%', px: 3 }}>
          {replyingTo && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              px: 2,
              py: 1,
              borderRadius: '10px',
              border: `1px solid ${DARK_COLORS.BORDER}`,
              backgroundColor: DARK_COLORS.ACCENT,
            }}>
              <Reply sx={{ fontSize: 16, color: DARK_COLORS.TEXT_SECONDARY }} />
              <Box sx={{ flex: 1, minWidth: 0, fontSize: '0.85rem', color: DARK_COLORS.TEXT_SECONDARY }}>
                Reply to: {(replyingTo.text || replyingTo.explanation || 'Message').slice(0, 80)}
              </Box>
              <IconButton
                size="small"
                onClick={handleClearReply}
                sx={{
                  color: DARK_COLORS.TEXT_SECONDARY,
                  '&:hover': { color: DARK_COLORS.TEXT_PRIMARY, backgroundColor: DARK_COLORS.HOVER },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: `1px solid ${DARK_COLORS.BORDER}`,
              borderRadius: '24px',
              backgroundColor: DARK_COLORS.ACCENT,
              paddingY: 1,
              paddingX: 2,
              overflow: "hidden",
              maxWidth: "100%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.6)",
              "&:focus-within": {
                borderColor: DARK_COLORS.TEXT_PRIMARY,
                boxShadow: `0 0 0 2px rgba(255, 255, 255, 0.2), 0 8px 32px rgba(0, 0, 0, 0.8)`,
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: DARK_COLORS.TEXT_SECONDARY,
                transform: "translateY(-1px)",
              }
            }}
          >
            <Box
              component="textarea"
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              ref={inputRefEl}
              rows={1}
              placeholder={t.typeMessage}
              sx={{
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                border: "none",
                outline: "none",
                minHeight: "80px",
                resize: "none",
                height: "80px",
                fontSize: "1rem",
                paddingY: "12px",
                paddingX: "16px",
                backgroundColor: DARK_COLORS.ACCENT,
                borderRadius: "20px",
                lineHeight: 1.5,
                fontFamily: "inherit",
                transition: "height 0.2s ease",
                color: DARK_COLORS.TEXT_PRIMARY,
                '&::placeholder': {
                  color: DARK_COLORS.TEXT_SECONDARY,
                  opacity: 0.8
                },
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: DARK_COLORS.BORDER,
                  borderRadius: '3px',
                  '&:hover': {
                    background: DARK_COLORS.TEXT_SECONDARY,
                  }
                }
              }}
            />
            <Tooltip title="Allega file">
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1,
                  color: DARK_COLORS.TEXT_SECONDARY,
                  '&:hover': { 
                    backgroundColor: DARK_COLORS.HOVER,
                    color: DARK_COLORS.TEXT_PRIMARY,
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <AttachFile fontSize="small" />
              </IconButton>
            </Tooltip>


            <Tooltip title="Microfono (in sviluppo)">
              <IconButton
                onClick={() => setShowPopup(true)}
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1,
                  color: DARK_COLORS.TEXT_SECONDARY,
                  '&:hover': { 
                    backgroundColor: DARK_COLORS.HOVER,
                    color: DARK_COLORS.TEXT_PRIMARY,
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Mic fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t.useWeb}>
              <IconButton
                onClick={() => setUseWeb(prev => !prev)}
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1,
                  color: useWeb ? DARK_COLORS.INFO : DARK_COLORS.TEXT_SECONDARY,
                  backgroundColor: useWeb ? `${DARK_COLORS.INFO}22` : 'transparent',
                  border: useWeb ? `1px solid ${DARK_COLORS.INFO}` : `1px solid ${DARK_COLORS.BORDER}`,
                  '&:hover': { 
                    backgroundColor: useWeb ? `${DARK_COLORS.INFO}33` : DARK_COLORS.HOVER,
                    color: useWeb ? DARK_COLORS.INFO : DARK_COLORS.TEXT_PRIMARY,
                    transform: 'scale(1.08)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Language fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t.suggestions}>
              <IconButton
                onClick={openQuickMenu}
                size="small"
                aria-label="Mostra suggerimenti"
                sx={{
                  color: "#FFD600",  
                  "&:hover": {
                    color: "#FFB300",  
                  }
                }}
              >
                <LightbulbOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>


            <QuickMenu
              anchorEl={menuAnchor}
              onClose={closeQuickMenu}
              suggestions={quickSuggestions}
              onPick={(t) => applySuggestion(t, false)}
              onDelete={handleDeleteSuggestion}
              onReorder={handleReorderSuggestions}
            />


            <SendButton
              onClick={handleSendMessage}
              disabled={isLoading || !input.trim()}
              sx={{
                width: 44,
                height: 44,
                minWidth: 0,
                borderRadius: '50%',
                backgroundColor: DARK_COLORS.PRIMARY,
                color: DARK_COLORS.TEXT_PRIMARY,
                marginLeft: "8px",
                border: `1px solid ${DARK_COLORS.BORDER}`,
                "&:hover": {
                  backgroundColor: DARK_COLORS.SECONDARY,
                  transform: "scale(1.1)",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.8)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
                "&:disabled": {
                  backgroundColor: DARK_COLORS.ACCENT,
                  color: DARK_COLORS.TEXT_SECONDARY,
                  opacity: 0.5,
                },
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
            >
              {isLoading ? (
                <AutoAwesomeIcon
                  sx={{
                    color: DARK_COLORS.TEXT_PRIMARY,
                    fontSize: 20,
                    animation: 'pulse 1.5s infinite ease-in-out',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(0.8)', opacity: 0.7 },
                      '50%': { transform: 'scale(1.2)', opacity: 1 },
                      '100%': { transform: 'scale(0.8)', opacity: 0.7 },
                    }
                  }}
                />
              ) : (
                <Send fontSize="small" sx={{ color: DARK_COLORS.TEXT_PRIMARY }} />
              )}
            </SendButton>
          </Box>
        </Box>
        <input type="file" ref={fileInputRef} style={{ display: "none" }} />
      </StyledInputWrapper>


      {chartData && chartData.suggestion && (
        <ChartModal
          open={true}
          onClose={() => setChartData(null)}
          headers={chartData.headers}
          rows={chartData.rows}
          x_axis={chartData.suggestion.x_axis}
          y_axis={chartData.suggestion.y_axis}
          chart_type={chartData.suggestion.chart_type}
        />
      )}
    </ChatMain>
  );
};


export default ChatMainView;
